import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useSwipeable } from 'react-swipeable';
import ModalAskFormSuggestion from './Modal/ModalAskFormSuggestion';

const SuggestedQuestionCards = ({ recommendedQuestions = [], sourceLogId }) => {
  const [selected, setSelected] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedCardText, setSelectedCardText] = useState('');
  
  // 스와이핑 관련 상태
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const containerRef = useRef(null);

  // 카드 너비 측정
  useEffect(() => {
    const updateCardWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setCardWidth(containerWidth);
      }
    };

    updateCardWidth();
    window.addEventListener('resize', updateCardWidth);
    return () => window.removeEventListener('resize', updateCardWidth);
  }, []);

  const handlers = useSwipeable({
    onSwipeStart: (eventData) => {
      // 세로 스와이프인지 확인
      const absX = Math.abs(eventData.deltaX);
      const absY = Math.abs(eventData.deltaY);
      
      // 세로 스와이프이면 카드 스와이프 무시
      if (absY > absX) {
        return;
      }
      
      setIsDragging(true);
    },
    onSwiping: (eventData) => {
      // 세로 스와이프인지 확인
      const absX = Math.abs(eventData.deltaX);
      const absY = Math.abs(eventData.deltaY);
      
      // 세로 스와이프이면 카드 스와이프 무시
      if (absY > absX) {
        return;
      }
      
      // 드래그 중일 때 오프셋 계산
      const deltaX = eventData.deltaX;
      const maxOffset = cardWidth * 0.3; // 최대 30%까지만 이동
      const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, deltaX));
      setDragOffset(clampedOffset);
    },
    onSwipedLeft: () => {
      if (currentIndex < recommendedQuestions.length - 1) {
        setCurrentIndex(prev => prev + 1);
      }
      setIsDragging(false);
      setDragOffset(0);
    },
    onSwipedRight: () => {
      if (currentIndex > 0) {
        setCurrentIndex(prev => prev - 1);
      }
      setIsDragging(false);
      setDragOffset(0);
    },
    onTouchEndOrOnMouseUp: () => {
      setIsDragging(false);
      setDragOffset(0);
    },
    trackTouch: true,
    preventScrollOnSwipe: false, // 세로 스크롤 허용
    delta: 15, // 최소 드래그 거리 증가로 세로 스크롤과 구분
  });

  const handleCardClick = (cardId, cardText) => {
    if (isDragging) return; // 드래그 중일 때는 클릭 방지
    setSelected(cardId);
    setSelectedCardText(cardText);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelected(null);
  };

  const handleConfirm = () => {
    setShowModal(false);
    setSelected(null);
  };

  // 카드 스타일 설정
  const getCardStyle = (index) => {
    if (index % 2 === 0) {
      return {
        borderColor: '#00F370',
        titleBorderColor: '#00F370',
        titleTextColor: 'rgba(0, 0, 0, 0.60)',
        titleActiveBg: '#E6FFF2',
      };
    }
    return {
      borderColor: '#0057FF',
      titleBorderColor: 'rgba(0, 87, 255, 0.50)',
      titleTextColor: 'rgba(0, 0, 0, 0.60)',
      titleActiveBg: '#E6F0FF',
    };
  };

  return (
    <Wrapper {...handlers}>
      <Container>
        <Header>
          <HeaderTitle>원하는 판례가 아니라면, 질문을 바꿔보세요.</HeaderTitle>
          <HeaderDescription>
            질문을 고르면 내용을 고칠 수 있어요. 다시 검색할 때 10로우를 사용해요.
          </HeaderDescription>
        </Header>

        <CardContainer 
          ref={containerRef}
          cardCount={recommendedQuestions.length}
          style={{
            transform: window.innerWidth <= 480 ? 
              `translateX(${-currentIndex * (100 / recommendedQuestions.length) + (isDragging ? (dragOffset / cardWidth) * (100 / recommendedQuestions.length) : 0)}%)` : 
              'none',
            transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {recommendedQuestions.map((question, idx) => {
            const isActive = selected === question.id;
            const isHovered = hoveredId === question.id;
            const isMobileActive = idx === currentIndex;
            const cardStyle = getCardStyle(idx);

            return (
              <CardWrapper 
                key={question.id} 
                isActive={isMobileActive}
                cardCount={recommendedQuestions.length}
              >
                <CardTitle
                  onClick={() => handleCardClick(question.id, question.question)}
                  onMouseEnter={() =>
                    window.innerWidth > 768 && setHoveredId(question.id)
                  }
                  onMouseLeave={() =>
                    window.innerWidth > 768 && setHoveredId(null)
                  }
                  isActive={isActive}
                  isHovered={isHovered}
                  borderColor={cardStyle.titleBorderColor}
                  activeBg={cardStyle.titleActiveBg}
                  textColor={cardStyle.titleTextColor}
                >
                  {question.type}
                </CardTitle>

                <Card
                  onClick={() => handleCardClick(question.id, question.question)}
                  onMouseEnter={() =>
                    window.innerWidth > 768 && setHoveredId(question.id)
                  }
                  onMouseLeave={() =>
                    window.innerWidth > 768 && setHoveredId(null)
                  }
                  isActive={isActive}
                  isHovered={isHovered}
                  borderColor={cardStyle.borderColor}
                >
                  <Text>{question.question}</Text>
                </Card>
              </CardWrapper>
            );
          })}
        </CardContainer>

        <Dots>
          {recommendedQuestions.map((_, idx) => (
            <Dot
              key={idx}
              isActive={currentIndex === idx}
              activeColor={idx === 0 ? '#00F370' : '#0057FF'}
            />
          ))}
        </Dots>
      </Container>

      <ModalAskFormSuggestion
        isOpen={showModal}
        onClose={handleModalClose}
        onConfirm={handleConfirm}
        initialText={selectedCardText}
        sourceLogId={sourceLogId}
      />
    </Wrapper>
  );
};

export default SuggestedQuestionCards;

const Wrapper = styled.div`
  width: 680px;
  height: auto;
  margin: 0 auto;
  border-radius: 24px;
  background: #f9f9f9;
  margin-bottom: 40px; /* 아래 컴포넌트와의 간격 */

  @media (max-width: 744px) {
    width: calc(100% - 64px);
    max-height: 489px;
    margin-bottom: 32px;
  }

  @media (max-width: 480px) {
    width: 100%;
    height: auto; /* 고정 높이 제거하여 콘텐츠에 맞게 조정 */
    max-height: none;
    margin-bottom: 24px;
    overflow: hidden;
  }
`;

const Container = styled.div`
  padding: 32px 24px;

  @media (max-width: 744px) {
    padding: 24px 20px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 50px;
  gap: 4px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 38px;
    gap: 4px;
  }
`;

const HeaderTitle = styled.span`
  color: rgba(0, 0, 0, 0.60);
  font-weight: 700;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px; 
  letter-spacing: -0.32px;
  white-space: pre-line; /* \n을 줄바꿈으로 처리 */
`;

const HeaderDescription = styled.span`
  color: rgba(0, 0, 0, 0.60);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
  letter-spacing: -0.28px;
  padding-top: 10px;

  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 20px; 
    letter-spacing: -0.26px;
  } 
`;

const CardContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;

  @media (max-width: 744px) {
    flex-direction: column;
    gap: 42px;
  }

  @media (max-width: 480px) {
    flex-direction: row;
    gap: 0;
    position: relative;
    width: ${({ cardCount }) => cardCount * 100}%;
    display: flex;
    flex-wrap: nowrap;
  }
`;

const CardWrapper = styled.div`
  position: relative;
  flex: 1;

  @media (max-width: 744px) {
    flex: 0 0 auto;
    display: block;
  }

  @media (max-width: 480px) {
    flex: 0 0 ${({ cardCount }) => 100 / cardCount}%;
    display: block;
    margin-left: 10px;
  }
`;

const CardTitle = styled.div`
  min-width: 160px;
  height: 36px;
  padding: 0 24px;
  gap: 10px;
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 56px;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: -0.26px;
  cursor: pointer;
  border: 1px solid ${({ borderColor }) => borderColor};
  background: ${({ isActive, isHovered, activeBg }) =>
    isActive || isHovered ? activeBg : '#fff'};
  color: ${({ textColor }) => textColor};
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 480px) {
    background: ${({ isActive, activeBg }) => (isActive ? activeBg : '#fff')};
    padding: 0 16px;
  }
`;

const Card = styled.div`
  padding: 34px 24px 24px 24px;
  border-radius: 24px;
  border: ${({ isActive, isHovered, borderColor }) =>
    isActive || isHovered
      ? `0.5px solid ${borderColor}`
      : '0.3px solid rgba(0, 0, 0, 0.20)'};
  background: #fff;
  transition: all 0.2s ease;
  cursor: pointer;

  @media (max-width: 768px) {
    border: ${({ isActive, borderColor }) =>
      isActive ? `0.5px solid ${borderColor}` : '0.3px solid rgba(0, 0, 0, 0.20)'};
    padding: 34px 18px 17px 18px;
  }
`;

const Text = styled.p`
  color: rgba(0, 0, 0, 0.60);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; 
  letter-spacing: -0.28px;
  margin: 0;
`;

const Dots = styled.div`
  display: none;

  @media (max-width: 480px) {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 24px;
  }
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ isActive, activeColor }) =>
    isActive ? activeColor : '#D9D9D9'};
`;
